import React from 'react';
import { DATA_SOURCES } from '../config';
import { formatValue, isMissing } from '../format';

/**
 * Footer describing where the page's data comes from, plus the Fed's current
 * balance-sheet runoff caps.
 *
 * The copy previously asserted the Fed's runoff caps as "$60B/month for
 * Treasuries and $35B for MBS" - correct when typed, silently wrong the moment
 * the committee changed them, and in any case a policy ceiling rather than a
 * description of what happened. It now reports the measured change in the
 * portfolio, which currently shows Treasury holdings rising even as MBS runs
 * off - something a fixed "maximum runoff" figure hid entirely.
 */
const DataSources = ({ marketData }) => {
  const treasuryChange = marketData?.data?.treasury_holdings_change_1m;
  const mbsChange = marketData?.data?.mbs_holdings_change_1m;
  const totalChange = marketData?.data?.total_holdings_change_1m;
  const haveChanges = !isMissing(treasuryChange) && !isMissing(mbsChange);

  return (
    <div className="mt-12 p-6 bg-gray-100 rounded-lg">
      <h3 className="font-semibold text-gray-900 mb-4">📚 Data Sources</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        {DATA_SOURCES.map((source) => (
          <div key={source.name}>
            <h4 className="font-medium text-gray-700 mb-2">{source.name}</h4>
            <ul className="text-gray-600 space-y-1">
              {source.series.map((series) => (
                <li key={series}>• {series}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">📈 Balance sheet, last month</h4>
        {haveChanges ? (
          <p className="text-sm text-blue-800">
            Treasury holdings{' '}
            <strong>{treasuryChange < 0 ? 'fell' : 'rose'} by {formatValue(Math.abs(treasuryChange), 'billions')}</strong>{' '}
            and MBS holdings{' '}
            <strong>{mbsChange < 0 ? 'fell' : 'rose'} by {formatValue(Math.abs(mbsChange), 'billions')}</strong>{' '}
            over the past month
            {!isMissing(totalChange) && (
              <>
                , a net{' '}
                <strong>
                  {totalChange < 0 ? 'reduction' : 'increase'} of {formatValue(Math.abs(totalChange), 'billions')}
                </strong>
              </>
            )}
            . A falling portfolio is quantitative tightening: the Fed is not fully reinvesting
            principal from maturing securities, so the market must absorb the supply.
          </p>
        ) : (
          <p className="text-sm text-blue-800">
            The change in the Fed's portfolio over the past month is unavailable from the data
            service.
          </p>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Refresh frequency varies by source. Values shown are whatever the data service last returned;
        fields it could not supply are marked unavailable rather than estimated.
      </p>
    </div>
  );
};

export default DataSources;
